import { config, ensureConfig } from './config';
import type { AuthChallenge, AuthSession, TokenPayload } from '../types/auth';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function buildUser(idToken: string) {
  const payload = parseJwt(idToken);
  return {
    email: String(payload?.email ?? payload?.['cognito:username'] ?? ''),
    username: String(payload?.['cognito:username'] ?? payload?.email ?? ''),
  };
}

export function getSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(config.storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

export function saveSessionFromTokens(tokens: TokenPayload): AuthSession {
  const existing = getSession();
  const session: AuthSession = {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? existing?.refreshToken,
    expiresAt: Date.now() + tokens.expires_in * 1000,
    loginAt: existing?.loginAt ?? Date.now(),
    user: buildUser(tokens.id_token),
  };
  window.localStorage.setItem(config.storageKey, JSON.stringify(session));
  return session;
}

export function clearSession(): void {
  window.localStorage.removeItem(config.storageKey);
}

/** Dispatched when an authenticated API call returns 401 (session cleared). */
export const AUTH_LOST_EVENT = 'sense-admin-auth-lost';

function notifyAuthLost(): void {
  clearSession();
  window.dispatchEvent(new CustomEvent(AUTH_LOST_EVENT));
}

export function isAuthenticated(): boolean {
  const session = getSession();
  return Boolean(session && Date.now() < session.expiresAt);
}

export function getIdToken(): string | null {
  const session = getSession();
  if (!session || Date.now() >= session.expiresAt) return null;
  return session.idToken;
}

async function adminFetch<T>(
  path: string,
  options: RequestInit = {},
  auth = true,
): Promise<T> {
  await ensureConfig();
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (auth) {
    const token = getIdToken();
    if (!token) throw new ApiError('Not authenticated');
    headers.set('Authorization', `Bearer ${token}`);
  }
  const resp = await fetch(`${config.apiUrl}${path}`, { ...options, headers });
  const data = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
  if (!resp.ok) {
    if (auth && resp.status === 401) {
      notifyAuthLost();
    }
    throw new ApiError(
      String(data.message || data.error_code || resp.statusText),
      typeof data.error_code === 'string' ? data.error_code : undefined,
      resp.status,
    );
  }
  return data as T;
}

export async function login(
  email: string,
  password: string,
): Promise<{ session?: AuthSession; challenge?: AuthChallenge }> {
  const data = await adminFetch<Record<string, unknown>>(
    '/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) },
    false,
  );
  if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
    return {
      challenge: {
        challenge: 'NEW_PASSWORD_REQUIRED',
        session: String(data.session),
        email: String(data.email || email),
      },
    };
  }
  const tokens: TokenPayload = {
    id_token: String(data.id_token),
    access_token: String(data.access_token),
    refresh_token: data.refresh_token ? String(data.refresh_token) : undefined,
    expires_in: Number(data.expires_in || 3600),
  };
  return { session: saveSessionFromTokens(tokens) };
}

export async function completeNewPassword(
  email: string,
  newPassword: string,
  session: string,
): Promise<AuthSession> {
  const data = await adminFetch<Record<string, unknown>>(
    '/auth/new-password',
    {
      method: 'POST',
      body: JSON.stringify({ email, new_password: newPassword, session }),
    },
    false,
  );
  return saveSessionFromTokens({
    id_token: String(data.id_token),
    access_token: String(data.access_token),
    refresh_token: data.refresh_token ? String(data.refresh_token) : undefined,
    expires_in: Number(data.expires_in || 3600),
  });
}

export { adminFetch };
